import {
  tpgIconDarkMode,
  tpgIconLightMode,
  tpgIconSettingsSystemDaydream,
} from '@tpg/grammar-web-icons';
import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, watch } from 'vue';

import { t } from '#ui-shared/i18n/i18n';

export enum DarkMode {
  Light = 'light',
  Dark = 'dark',
  System = 'system',
}

export const useDarkModeStore = defineStore('darkMode', () => {
  const darkMode = useLocalStorage<DarkMode>('darkMode', DarkMode.Dark);

  const media = window.matchMedia('(prefers-color-scheme: dark)');

  const isDark = computed<boolean>((): boolean => {
    if (darkMode.value === DarkMode.System) {
      return media.matches;
    }

    return darkMode.value === DarkMode.Dark;
  });

  const darkModeLabel = computed(() => {
    switch (darkMode.value) {
      case DarkMode.Dark:
        return t('lightMode');
      case DarkMode.Light:
        return t('darkMode');
      default:
        return t('systemDefault');
    }
  });

  const darkModeIcon = computed(() => {
    switch (darkMode.value) {
      case DarkMode.Dark:
        return tpgIconLightMode;
      case DarkMode.Light:
        return tpgIconDarkMode;
      default:
        return tpgIconSettingsSystemDaydream;
    }
  });

  const setDarkMode = (mode: DarkMode | string): void => {
    if (Object.values(DarkMode).includes(mode as DarkMode)) {
      darkMode.value = mode as DarkMode;
    }
    else {
      console.warn(`Invalid dark mode value: ${mode}`);
    }
  };

  const updateDom = (mode: DarkMode.Dark | DarkMode.Light): void => {
    if (mode === DarkMode.Dark) {
      document.documentElement.classList.remove('theme-light');
      document.documentElement.classList.add('theme-dark');
      document.documentElement.setAttribute('data-theme', 'dark');
    }
    else if (mode === DarkMode.Light) {
      document.documentElement.classList.remove('theme-dark');
      document.documentElement.classList.add('theme-light');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  };

  const onSystemColorChange = (): void => {
    if (darkMode.value !== DarkMode.System) {
      return;
    }

    updateDom(media.matches ? DarkMode.Dark : DarkMode.Light);
  };

  watch(
    darkMode,
    () => {
      if (darkMode.value === DarkMode.System) {
        onSystemColorChange();
      }
      else {
        updateDom(darkMode.value);
      }
    },
    {
      immediate: true,
    },
  );

  media.addEventListener('change', onSystemColorChange);

  return { darkMode, isDark, darkModeLabel, darkModeIcon, setDarkMode };
});
